/**
 * App Download Page Configurations
 *
 * Data-driven config for the generic AppDownload component.
 * To add a new app's download page, just add an entry here.
 *
 * IMPORTANT: All Tailwind class names must be written as full strings
 * (not constructed dynamically) so the JIT compiler can detect them.
 */

import type { AppListing } from './listingToConfig.ts'
import { listingToConfig } from './listingToConfig.ts'
import phidiasListing from '../content/phidias/appstore-listing.json' with { type: 'json' }

export type IconKey =
  | 'mic' | 'book-open' | 'heart' | 'shield' | 'sparkles'
  | 'bot' | 'workflow' | 'brain' | 'git-branch' | 'zap'
  | 'camera' | 'music' | 'star' | 'users' | 'gift'
  | 'rings' | 'image' | 'video' | 'lock' | 'cloud'

export interface FeatureConfig {
  iconKey: IconKey
  title: string
  description: string
}

export interface TrustBadge {
  value: string
  label: string
}

/** Pre-resolved Tailwind classes for theming. All strings must be complete class names. */
export interface ThemeClasses {
  /** Gradient text for the app title: e.g. "bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent" */
  titleGradient: string
  /** Hero background overlay: e.g. "bg-gradient-to-br from-purple-900/50 via-purple-900/30 to-transparent" */
  heroBg: string
  /** Icon shadow: e.g. "shadow-purple-500/30" */
  iconShadow: string
  /** Icon fallback gradient bg: e.g. "from-blue-500 via-purple-500 to-pink-500" */
  iconFallbackGradient: string
  /** Feature card hover border: e.g. "hover:border-purple-500/50" */
  featureBorder: string
  /** Accent text color: e.g. "text-purple-400" */
  accentText: string
  /** Step number bg: e.g. "bg-purple-500" */
  stepBg: string
}

export interface AppDownloadConfig {
  appId: string
  name: string
  tagline: string
  description: string
  theme: ThemeClasses
  iconPath: string
  downloadPath: string
  iosManifestPath: string
  defaultVersion: string
  defaultFileSize: string
  features: FeatureConfig[]
  trustBadges: TrustBadge[]
  androidFinalStep: string
  iosFinalStep: string
  windowsFinalStep?: string
  /** Shown on the Web tab's final install step (e.g. "Add to Home Screen" guidance). */
  webFinalStep?: string
  unknownSourceNote: string
  completionMessage: string
  /** Show the Windows download tab even before the first CI build uploads a version */
  windowsEnabled?: boolean
  /** Show the Web (PWA) tab — no download/build required, just a live URL. */
  webEnabled?: boolean
  /** The PWA's live URL, e.g. "https://harvey.multiskilled.ai/app/". Required when webEnabled. */
  webUrl?: string
  /** Who the app is for, in the owner's words. Shown under the description. */
  audience?: string
  /** Replaces the default features heading ("Why People Love <name>"). Set it when the page must not claim popularity. */
  featuresHeading?: string
  /** Things the app declares or does not do that a reader should know. Shown in full, never dropped. */
  knownLimits?: string[]
  /** Replaces the generic Android install steps when present. Owner's words, in order. */
  installSteps?: string[]
  /** Platform line, e.g. "Android (arm64-v8a, x86_64)". */
  platformNote?: string
  /** What the owner says about iOS. When present it replaces the generic "iOS Coming Soon" block. */
  iosStatement?: string
  screenshots?: { src: string; caption: string }[]
  /** The version the listing describes, and its sha256, shown only while that is the version being offered. */
  listingVersion?: string
  /** The version code of that version; used to build the fallback download link when the API is unreachable. */
  defaultVersionCode?: number
  sha256?: string
  /** Where the page content came from (repo and commit). Shown in the footer. */
  contentSource?: string
}

/** Default theme for apps without custom branding */
const DEFAULT_THEME: ThemeClasses = {
  titleGradient: 'bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent',
  heroBg: 'bg-gradient-to-br from-emerald-900/50 via-teal-900/30 to-transparent',
  iconShadow: 'shadow-emerald-500/30',
  iconFallbackGradient: 'from-emerald-500 via-teal-500 to-cyan-500',
  featureBorder: 'hover:border-emerald-500/50',
  accentText: 'text-emerald-400',
  stepBg: 'bg-emerald-500',
}

/**
 * Generate a default config for any app not in APP_CONFIGS.
 * Uses API data (name, description) when available.
 */
export function getConfigForApp(
  appId: string,
  apiData?: { name?: string; description?: string; icon_url?: string }
): AppDownloadConfig {
  if (APP_CONFIGS[appId]) return APP_CONFIGS[appId]

  const name = apiData?.name || appId.charAt(0).toUpperCase() + appId.slice(1)
  return {
    appId,
    name,
    tagline: apiData?.description?.slice(0, 80) || `Download ${name}`,
    description: apiData?.description || `Install ${name} on your device.`,
    theme: DEFAULT_THEME,
    iconPath: apiData?.icon_url || `/download/mskai-apps/${appId}/icon.png`,
    downloadPath: `/download/mskai-apps/android/${appId}`,
    iosManifestPath: `/api/manifest/${appId}/ios`,
    defaultVersion: 'latest',
    defaultFileSize: 'N/A',
    features: [
      { iconKey: 'shield', title: 'Secure', description: 'Enterprise-grade encryption protects your data.' },
      { iconKey: 'zap', title: 'Fast', description: 'Optimized for performance on mobile and desktop.' },
      { iconKey: 'cloud', title: 'Synced', description: 'Your data stays in sync across all your devices.' },
    ],
    trustBadges: [
      { value: '256-bit', label: 'Encryption' },
      { value: 'SSO', label: 'Authentication' },
    ],
    androidFinalStep: `Open ${name} and sign in to get started.`,
    iosFinalStep: `Find ${name} on your home screen and sign in.`,
    unknownSourceNote: `This is safe for trusted enterprise apps like ${name}.`,
    completionMessage: `You're all set — ${name} is ready to use.`,
  }
}

export const APP_CONFIGS: Record<string, AppDownloadConfig> = {
  harvey: {
    appId: 'harvey',
    name: 'Harvey',
    tagline: 'Your Sovereign AI Companion — Always Present, Always Yours',
    description:
      'Harvey inverts the stack: your device becomes the sovereign root of your intelligence. An invisible, benevolent companion — loyal, ever-present, real in its effects — that processes decisions locally and keeps you in control of every AI action.',
    theme: {
      titleGradient: 'bg-gradient-to-r from-blue-400 via-sky-400 to-cyan-400 bg-clip-text text-transparent',
      heroBg: 'bg-gradient-to-br from-blue-900/50 via-sky-900/30 to-transparent',
      iconShadow: 'shadow-blue-500/30',
      iconFallbackGradient: 'from-blue-500 via-sky-500 to-cyan-500',
      featureBorder: 'hover:border-blue-500/50',
      accentText: 'text-blue-400',
      stepBg: 'bg-blue-500',
    },
    iconPath: '/downloads/mskai-apps/harvey/icon.png',
    downloadPath: '/downloads/mskai-apps/android/harvey',
    iosManifestPath: '/api/manifest/harvey/ios',
    defaultVersion: '1.1.0',
    defaultFileSize: 'N/A',
    features: [
      {
        iconKey: 'shield',
        title: 'Cryptographic Identity',
        description: 'Every device gets a real Ed25519 did:key, bound to your Authentik identity via OIDC challenge-response. UCAN-scoped capability tokens for every tool. Zero trust by default.',
      },
      {
        iconKey: 'brain',
        title: 'HITL-D Intelligence',
        description: 'Human-in-the-loop AI with 5-tier trust delegation. Harvey routes decisions to the right operator at the right tier — backed by SBAR enrichment from DANAI and your local model.',
      },
      {
        iconKey: 'zap',
        title: 'Offline-First Queue',
        description: 'Works without connectivity. LAN-first sync between phone and desktop ensures your queue is always fresh, with IndexedDB offline cache on the PWA.',
      },
      {
        iconKey: 'users',
        title: 'Team Coordination',
        description: 'Skill-based assignment routing, operator approval flow, presence tracking, and real-time collaboration with push notifications across your team.',
      },
      {
        iconKey: 'bot',
        title: 'Ecosystem Native',
        description: 'Deep integration with Coherence Engine, DANAI sovereign LLM, MMB voice, and Heyday recommendations. Web4 peer cooperative mesh and federated intelligence are on the roadmap.',
      },
      {
        iconKey: 'sparkles',
        title: 'Research & Audit',
        description: 'Magentic-UI deep-web research for DECISION_REQUIRED items. EU AI Act audit packages signed with Ed25519. Every data outflow has a W3C consent receipt.',
      },
    ],
    trustBadges: [
      { value: 'Ed25519', label: 'Signed' },
      { value: 'UCAN', label: 'Scoped' },
      { value: 'HITL-D', label: 'Governed' },
      { value: 'W3C VC', label: 'Verifiable' },
    ],
    androidFinalStep: 'Open Harvey from your app drawer, enter your relay URL and API key. Use the Identity tab to bind your device to your Authentik account.',
    iosFinalStep: 'Find Harvey on your home screen, enter your relay URL and API key, then link your identity from Settings.',
    windowsFinalStep: 'Harvey starts in the system tray. Right-click the tray icon → Open Dashboard, then enter your relay URL and API key. Use Settings → Identity to bind your Authentik account.',
    webFinalStep: 'Harvey opens in your browser with your queue already connected. Add it to your home screen (or install it as an app) for offline-first access with IndexedDB caching.',
    unknownSourceNote: 'Harvey is a trusted Coherence ecosystem app. This is safe to install.',
    completionMessage: 'Harvey is ready — connect to your relay, bind your identity, and start processing your AI decision queue.',
    windowsEnabled: true,
    webEnabled: true,
    webUrl: 'https://harvey.multiskilled.ai/app/',
  },

  mmb: {
    appId: 'mmb',
    name: 'MMB',
    tagline: 'Preserve Your Family\'s Stories Forever',
    description:
      'Record, transcribe, and organize your family\'s most precious memories with AI-powered interview guidance. Create a lasting legacy for generations to come.',
    theme: {
      titleGradient: 'bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent',
      heroBg: 'bg-gradient-to-br from-purple-900/50 via-purple-900/30 to-transparent',
      iconShadow: 'shadow-purple-500/30',
      iconFallbackGradient: 'from-blue-500 via-purple-500 to-pink-500',
      featureBorder: 'hover:border-purple-500/50',
      accentText: 'text-purple-400',
      stepBg: 'bg-purple-500',
    },
    iconPath: '/download/mskai-apps/mmb/icon.png',
    downloadPath: '/download/mskai-apps/android/mmb',
    iosManifestPath: '/api/manifest/mmb/ios',
    defaultVersion: 'latest',
    defaultFileSize: 'N/A',
    features: [
      {
        iconKey: 'mic',
        title: 'AI Interview Guidance',
        description: 'Intelligent prompts help you ask the right questions to capture meaningful family stories.',
      },
      {
        iconKey: 'book-open',
        title: 'Automatic Transcription',
        description: 'Recordings are automatically transcribed, making your stories searchable and preservable.',
      },
      {
        iconKey: 'heart',
        title: 'Family Tree Integration',
        description: 'Link stories to family members and build a rich tapestry of your family history.',
      },
      {
        iconKey: 'shield',
        title: 'Private & Secure',
        description: 'Your family memories are encrypted and stored securely. You control who has access.',
      },
      {
        iconKey: 'sparkles',
        title: 'Memory Preservation',
        description: 'Pass down wisdom, experiences, and love to future generations.',
      },
    ],
    trustBadges: [
      { value: '256-bit', label: 'Encryption' },
      { value: '100%', label: 'Private' },
      { value: 'GDPR', label: 'Compliant' },
      { value: '24/7', label: 'Support' },
    ],
    androidFinalStep: 'Open MMB from your app drawer and sign in to start preserving memories.',
    iosFinalStep: 'Find MMB on your home screen and open it to start preserving memories.',
    unknownSourceNote: 'This is safe for trusted apps like MMB.',
    completionMessage: 'Start recording your first family interview and preserve those precious memories.',
  },

  danai: {
    appId: 'danai',
    name: 'DANAI',
    tagline: 'Distributed AI Agent Network & Intelligence',
    description:
      'Your AI-powered command center for orchestrating intelligent agents, automating workflows, and achieving more with less effort. Enterprise-grade AI at your fingertips.',
    theme: {
      titleGradient: 'bg-gradient-to-r from-indigo-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent',
      heroBg: 'bg-gradient-to-br from-indigo-900/50 via-blue-900/30 to-transparent',
      iconShadow: 'shadow-indigo-500/30',
      iconFallbackGradient: 'from-indigo-500 via-blue-500 to-cyan-500',
      featureBorder: 'hover:border-indigo-500/50',
      accentText: 'text-indigo-400',
      stepBg: 'bg-indigo-500',
    },
    iconPath: '/download/mskai-apps/danai/icon.png',
    downloadPath: '/download/mskai-apps/android/danai',
    iosManifestPath: '/api/manifest/danai/ios',
    defaultVersion: 'latest',
    defaultFileSize: 'N/A',
    features: [
      {
        iconKey: 'bot',
        title: 'AI Agent Orchestration',
        description: 'Deploy and manage AI agents that work autonomously on your behalf, coordinating complex multi-step tasks.',
      },
      {
        iconKey: 'workflow',
        title: 'Intelligent Workflows',
        description: 'Create sophisticated workflows that adapt in real-time, learning from outcomes to improve performance.',
      },
      {
        iconKey: 'brain',
        title: 'Cognitive Processing',
        description: 'Leverage advanced AI models for natural language understanding, reasoning, and decision-making.',
      },
      {
        iconKey: 'git-branch',
        title: 'Version Control Integration',
        description: 'Seamlessly connect with Git repositories for code analysis, PR reviews, and automated commits.',
      },
      {
        iconKey: 'shield',
        title: 'Enterprise Security',
        description: 'Bank-grade encryption, SSO authentication, and comprehensive audit trails for compliance.',
      },
      {
        iconKey: 'zap',
        title: 'Real-time Sync',
        description: 'Stay connected with WebSocket-powered updates, notifications, and live agent status monitoring.',
      },
    ],
    trustBadges: [
      { value: 'AES-256', label: 'Encryption' },
      { value: 'SSO', label: 'Authentication' },
      { value: 'SOC2', label: 'Compliant' },
      { value: '24/7', label: 'Monitoring' },
    ],
    androidFinalStep: 'Open DANAI from your app drawer and sign in with your enterprise SSO credentials.',
    iosFinalStep: 'Find DANAI on your home screen and sign in with your enterprise credentials.',
    unknownSourceNote: 'This is safe for enterprise apps like DANAI.',
    completionMessage: 'Launch DANAI and sign in with your enterprise SSO credentials to start orchestrating AI agents.',
  },

  wedlegacy: {
    appId: 'wedlegacy',
    name: 'WedLegacy',
    tagline: 'Capture Your Wedding Story Forever',
    description:
      'Record heartfelt messages from guests, capture candid moments, and preserve your wedding day memories with AI-powered interviews and beautiful storytelling.',
    theme: {
      titleGradient: 'bg-gradient-to-r from-rose-400 via-pink-400 to-amber-400 bg-clip-text text-transparent',
      heroBg: 'bg-gradient-to-br from-rose-900/50 via-pink-900/30 to-transparent',
      iconShadow: 'shadow-rose-500/30',
      iconFallbackGradient: 'from-rose-500 via-pink-500 to-amber-500',
      featureBorder: 'hover:border-rose-500/50',
      accentText: 'text-rose-400',
      stepBg: 'bg-rose-500',
    },
    iconPath: '/download/mskai-apps/wedlegacy/icon.png',
    downloadPath: '/download/mskai-apps/android/wedlegacy',
    iosManifestPath: '/api/manifest/wedlegacy/ios',
    defaultVersion: 'latest',
    defaultFileSize: 'N/A',
    features: [
      {
        iconKey: 'mic',
        title: 'Guest Messages',
        description: 'Let wedding guests record heartfelt voice messages and stories for the happy couple.',
      },
      {
        iconKey: 'camera',
        title: 'Photo & Video Capture',
        description: 'Collect photos and videos from every guest into one beautiful, organized collection.',
      },
      {
        iconKey: 'heart',
        title: 'Love Story Timeline',
        description: 'Build a chronological timeline of your relationship journey from first meeting to "I do".',
      },
      {
        iconKey: 'book-open',
        title: 'Auto Transcription',
        description: 'Every recorded message is automatically transcribed into searchable, shareable text.',
      },
      {
        iconKey: 'shield',
        title: 'Private & Secure',
        description: 'Your wedding memories are encrypted and shared only with people you choose.',
      },
    ],
    trustBadges: [
      { value: '256-bit', label: 'Encryption' },
      { value: '100%', label: 'Private' },
      { value: 'GDPR', label: 'Compliant' },
      { value: '24/7', label: 'Support' },
    ],
    androidFinalStep: 'Open WedLegacy from your app drawer and sign in to start capturing wedding memories.',
    iosFinalStep: 'Find WedLegacy on your home screen and open it to start capturing wedding memories.',
    unknownSourceNote: 'This is safe for trusted apps like WedLegacy.',
    completionMessage: 'Start capturing your wedding memories — guest messages, photos, and stories all in one place.',
  },

  sentinel: {
    appId: 'sentinel',
    name: 'Sentinel',
    tagline: 'AI-Orchestrated Insurance Intelligence',
    description:
      'Sentinel Protocol transforms insurance through autonomous AI agents, real-time market intelligence, and coherent multi-system coordination. Enterprise-grade insurance intelligence at your fingertips.',
    theme: {
      titleGradient: 'bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent',
      heroBg: 'bg-gradient-to-br from-blue-900/50 via-cyan-900/30 to-transparent',
      iconShadow: 'shadow-blue-500/30',
      iconFallbackGradient: 'from-blue-500 via-cyan-500 to-teal-500',
      featureBorder: 'hover:border-blue-500/50',
      accentText: 'text-blue-400',
      stepBg: 'bg-blue-500',
    },
    iconPath: '/download/mskai-apps/sentinel/icon.png',
    downloadPath: '/download/mskai-apps/android/sentinel',
    iosManifestPath: '/api/manifest/sentinel/ios',
    defaultVersion: 'latest',
    defaultFileSize: 'N/A',
    features: [
      {
        iconKey: 'bot',
        title: 'Autonomous Agent Swarms',
        description: 'Deploy intelligent agent networks that coordinate complex insurance workflows without manual intervention.',
      },
      {
        iconKey: 'brain',
        title: 'Market Intelligence',
        description: 'Real-time analysis of insurance markets, risk factors, and competitive positioning.',
      },
      {
        iconKey: 'shield',
        title: 'Risk Assessment',
        description: 'AI-powered underwriting and risk scoring with explainable decision trails.',
      },
      {
        iconKey: 'workflow',
        title: 'Claims Automation',
        description: 'Streamlined claims processing with AI triage, fraud detection, and settlement recommendations.',
      },
      {
        iconKey: 'git-branch',
        title: 'Multi-System Coordination',
        description: 'Seamless integration across policy management, CRM, billing, and regulatory reporting systems.',
      },
    ],
    trustBadges: [
      { value: 'AES-256', label: 'Encryption' },
      { value: 'SOC2', label: 'Compliant' },
      { value: 'GDPR', label: 'Ready' },
      { value: '24/7', label: 'Monitoring' },
    ],
    androidFinalStep: 'Open Sentinel from your app drawer and sign in with your enterprise credentials.',
    iosFinalStep: 'Find Sentinel on your home screen and sign in with your enterprise credentials.',
    unknownSourceNote: 'This is safe for enterprise apps like Sentinel Protocol.',
    completionMessage: 'Sign in to Sentinel and start orchestrating your insurance intelligence workflows.',
  },

  famulus: {
    appId: 'famulus',
    name: 'Famulus',
    tagline: 'Your Multi-Agent AI Orchestration Platform',
    description:
      'Famulus — from the Latin for personal attendant — is your AI-powered assistant platform for orchestrating multi-agent workflows, automating complex tasks, and amplifying human capability across the Coherence ecosystem.',
    theme: {
      titleGradient: 'bg-gradient-to-r from-teal-400 via-emerald-400 to-green-400 bg-clip-text text-transparent',
      heroBg: 'bg-gradient-to-br from-teal-900/50 via-emerald-900/30 to-transparent',
      iconShadow: 'shadow-teal-500/30',
      iconFallbackGradient: 'from-teal-500 via-emerald-500 to-green-500',
      featureBorder: 'hover:border-teal-500/50',
      accentText: 'text-teal-400',
      stepBg: 'bg-teal-500',
    },
    iconPath: '/download/mskai-apps/famulus/icon.png',
    downloadPath: '/download/mskai-apps/android/famulus',
    iosManifestPath: '/api/manifest/famulus/ios',
    defaultVersion: 'latest',
    defaultFileSize: 'N/A',
    features: [
      {
        iconKey: 'bot',
        title: 'AI Agent Orchestration',
        description: 'Coordinate multiple AI agents working in parallel on complex, multi-step tasks with shared context.',
      },
      {
        iconKey: 'workflow',
        title: 'Workflow Automation',
        description: 'Build intelligent pipelines that adapt, learn from outcomes, and improve over time.',
      },
      {
        iconKey: 'sparkles',
        title: 'Personal Intelligence',
        description: 'Your own AI attendant — learns your preferences, anticipates needs, handles routine tasks.',
      },
      {
        iconKey: 'shield',
        title: 'Private & Secure',
        description: 'Self-hosted on the Coherence infrastructure. Your data never leaves your environment.',
      },
      {
        iconKey: 'zap',
        title: 'Coherence Native',
        description: 'Deep integration with the Coherence ecosystem — shares context across all your AI tools.',
      },
    ],
    trustBadges: [
      { value: '100%', label: 'Self-Hosted' },
      { value: 'AES-256', label: 'Encryption' },
      { value: 'Open', label: 'Source' },
      { value: 'GDPR', label: 'Compliant' },
    ],
    androidFinalStep: 'Open Famulus from your app drawer and sign in to start orchestrating your AI agents.',
    iosFinalStep: 'Find Famulus on your home screen and sign in to activate your AI attendant.',
    unknownSourceNote: 'This is safe for trusted enterprise apps like Famulus.',
    completionMessage: 'Your AI attendant is ready — sign in to Famulus and start your first workflow.',
  },

  symphony: {
    appId: 'symphony',
    name: 'Symphony',
    tagline: 'Coherence Ecosystem Orchestration',
    description:
      'Symphony is the harmonizing layer of the Coherence ecosystem — coordinating services, synchronizing state, and ensuring every component plays in tune. The conductor for your distributed AI infrastructure.',
    theme: {
      titleGradient: 'bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400 bg-clip-text text-transparent',
      heroBg: 'bg-gradient-to-br from-violet-900/50 via-purple-900/30 to-transparent',
      iconShadow: 'shadow-violet-500/30',
      iconFallbackGradient: 'from-violet-500 via-purple-500 to-fuchsia-500',
      featureBorder: 'hover:border-violet-500/50',
      accentText: 'text-violet-400',
      stepBg: 'bg-violet-500',
    },
    iconPath: '/download/mskai-apps/symphony/icon.png',
    downloadPath: '/download/mskai-apps/android/symphony',
    iosManifestPath: '/api/manifest/symphony/ios',
    defaultVersion: 'latest',
    defaultFileSize: 'N/A',
    features: [
      {
        iconKey: 'workflow',
        title: 'Service Orchestration',
        description: 'Coordinate distributed services with intelligent routing, load balancing, and health management.',
      },
      {
        iconKey: 'zap',
        title: 'Real-time Sync',
        description: 'Keep state consistent across all ecosystem components with event-driven synchronization.',
      },
      {
        iconKey: 'git-branch',
        title: 'Dependency Management',
        description: 'Automatically resolve service dependencies, manage startup order, and handle graceful degradation.',
      },
      {
        iconKey: 'shield',
        title: 'Resilience Patterns',
        description: 'Circuit breakers, retry logic, and bulkhead patterns built in for production reliability.',
      },
      {
        iconKey: 'brain',
        title: 'Intelligent Routing',
        description: 'AI-assisted traffic routing based on performance metrics, capacity, and business rules.',
      },
    ],
    trustBadges: [
      { value: '99.9%', label: 'Uptime' },
      { value: 'mTLS', label: 'Security' },
      { value: 'Open', label: 'Standards' },
      { value: '24/7', label: 'Monitoring' },
    ],
    androidFinalStep: 'Open Symphony from your app drawer and connect to your Coherence infrastructure.',
    iosFinalStep: 'Find Symphony on your home screen and connect to your Coherence infrastructure.',
    unknownSourceNote: 'This is safe for trusted ecosystem apps like Symphony.',
    completionMessage: 'Symphony is ready — connect to your Coherence infrastructure to begin orchestration.',
  },

  gc: {
    appId: 'gc',
    name: 'Ground Control',
    tagline: 'Human-in-the-Loop AI Agent Management',
    description:
      'Ground Control Ultra is your mission control for AI agent operations — HITL approval workflows, trust progression, 5-tier autonomy governance, and real-time oversight across the entire Coherence AI fleet.',
    theme: {
      titleGradient: 'bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent',
      heroBg: 'bg-gradient-to-br from-amber-900/50 via-orange-900/30 to-transparent',
      iconShadow: 'shadow-amber-500/30',
      iconFallbackGradient: 'from-amber-500 via-orange-500 to-red-500',
      featureBorder: 'hover:border-amber-500/50',
      accentText: 'text-amber-400',
      stepBg: 'bg-amber-500',
    },
    iconPath: '/download/mskai-apps/gc/icon.png',
    downloadPath: '/download/mskai-apps/android/gc',
    iosManifestPath: '/api/manifest/gc/ios',
    defaultVersion: 'latest',
    defaultFileSize: 'N/A',
    features: [
      {
        iconKey: 'users',
        title: 'HITL Approvals',
        description: 'Review, approve, or override AI agent actions before they execute. Bulk operations supported.',
      },
      {
        iconKey: 'star',
        title: '5-Tier Trust Progression',
        description: 'Graduated autonomy from fully supervised to fully autonomous — promote agents as they earn trust.',
      },
      {
        iconKey: 'shield',
        title: 'Governance Dashboard',
        description: 'Real-time visibility into every AI action, with audit trails and compliance reporting.',
      },
      {
        iconKey: 'bot',
        title: 'Agent Fleet Management',
        description: 'Monitor, pause, redirect, and retrain agents across the entire Coherence ecosystem.',
      },
      {
        iconKey: 'workflow',
        title: 'External Communications',
        description: 'Secure outbound communications gateway with ECA protocol for cross-ecosystem messaging.',
      },
    ],
    trustBadges: [
      { value: 'HITL', label: 'Governance' },
      { value: 'AES-256', label: 'Encryption' },
      { value: 'Full', label: 'Audit Trail' },
      { value: '5-Tier', label: 'Trust Model' },
    ],
    androidFinalStep: 'Open Ground Control from your app drawer and sign in to your mission control dashboard.',
    iosFinalStep: 'Find Ground Control on your home screen and sign in to your mission control dashboard.',
    unknownSourceNote: 'This is safe for enterprise apps like Ground Control Ultra.',
    completionMessage: 'Ground Control is online — sign in to review your AI agent queue and approval dashboard.',
  },

  wb2: {
    appId: 'wb2',
    name: 'WB2 Platform',
    tagline: 'AI-Powered Brand Acquisition Intelligence',
    description:
      'WB2 Platform is a comprehensive acquisition analysis and workflow intelligence system. Apply Buffett-style investment scoring to brand targets, govern decisions with HITL approval workflows, and integrate AI-powered competitive intelligence.',
    theme: {
      titleGradient: 'bg-gradient-to-r from-lime-400 via-green-400 to-emerald-400 bg-clip-text text-transparent',
      heroBg: 'bg-gradient-to-br from-lime-900/50 via-green-900/30 to-transparent',
      iconShadow: 'shadow-lime-500/30',
      iconFallbackGradient: 'from-lime-500 via-green-500 to-emerald-500',
      featureBorder: 'hover:border-lime-500/50',
      accentText: 'text-lime-400',
      stepBg: 'bg-lime-500',
    },
    iconPath: '/download/mskai-apps/wb2/icon.png',
    downloadPath: '/download/mskai-apps/android/wb2',
    iosManifestPath: '/api/manifest/wb2/ios',
    defaultVersion: 'latest',
    defaultFileSize: 'N/A',
    features: [
      {
        iconKey: 'brain',
        title: 'Buffett Scoring',
        description: 'AI-powered target analysis using Warren Buffett\'s investment principles — moat, management, margin.',
      },
      {
        iconKey: 'users',
        title: 'HITL Approval System',
        description: 'Human-in-the-loop governance for acquisition decisions with multi-stage review workflows.',
      },
      {
        iconKey: 'star',
        title: 'Competitive Intelligence',
        description: 'Real-time market monitoring, competitor benchmarking, and opportunity scoring.',
      },
      {
        iconKey: 'workflow',
        title: 'Acquisition Pipeline',
        description: 'Track targets from discovery through due diligence to close with integrated task management.',
      },
      {
        iconKey: 'shield',
        title: 'Sentinel Integration',
        description: 'Deep integration with Sentinel Protocol for insurance and risk analysis on target brands.',
      },
    ],
    trustBadges: [
      { value: 'AES-256', label: 'Encryption' },
      { value: 'HITL', label: 'Governance' },
      { value: 'SOC2', label: 'Compliant' },
      { value: 'Local AI', label: 'First' },
    ],
    androidFinalStep: 'Open WB2 Platform from your app drawer and sign in to your acquisition dashboard.',
    iosFinalStep: 'Find WB2 Platform on your home screen and sign in to your acquisition dashboard.',
    unknownSourceNote: 'This is safe for enterprise apps like WB2 Platform.',
    completionMessage: 'WB2 Platform is ready — sign in to review your acquisition pipeline and target scoring.',
  },
  // Built from phidias's OWN listing (vendored snapshot, see src/content/phidias/SOURCE.md). phidias owns the words.
  phidias: listingToConfig(
    phidiasListing as AppListing,
    '/store/phidias',
    'phidias b7e3f22 (mobile/store/appstore-listing.json)',
  ),
}
